import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import { progressAPI } from '../../../services/api';
import type { ActiveWorkoutSession, ActiveExercise, ActiveSet } from '../../../types';

function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key); }
  catch { return null; }
}
function safeSessionRemove(key: string) {
  try { sessionStorage.removeItem(key); } catch { /* noop */ }
}
function safeSessionSet(key: string, value: string) {
  try { sessionStorage.setItem(key, value); } catch { /* noop */ }
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

export default function WorkoutLogSession() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState<ActiveWorkoutSession>({
    startedAt: new Date().toISOString(),
    name: 'Workout',
    exercises: [],
  });
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const saveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load draft from sessionStorage on mount
  useEffect(() => {
    const draft = safeSessionGet('activeWorkoutDraft');
    if (draft) {
      try {
        const parsed: ActiveWorkoutSession = JSON.parse(draft);
        setSession(parsed);
      } catch { /* ignore corrupt draft */ }
    }
    // Start elapsed timer
    timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Debounced sessionStorage persistence
  useEffect(() => {
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      safeSessionSet('activeWorkoutDraft', JSON.stringify(session));
    }, 500);
    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [session]);

  const updateExercise = (exId: string, updater: (ex: ActiveExercise) => ActiveExercise) => {
    setSession(prev => ({
      ...prev,
      exercises: prev.exercises.map(ex => ex.id === exId ? updater(ex) : ex),
    }));
  };

  const addSet = (exId: string) => {
    updateExercise(exId, ex => ({
      ...ex,
      sets: [...ex.sets, { reps: 0, weight: 0, completed: false }],
    }));
  };

  const updateSet = (exId: string, setIndex: number, field: keyof ActiveSet, value: number | boolean) => {
    updateExercise(exId, ex => ({
      ...ex,
      sets: ex.sets.map((s, i) => i === setIndex ? { ...s, [field]: value } : s),
    }));
  };

  const removeSet = (exId: string, setIndex: number) => {
    updateExercise(exId, ex => ({
      ...ex,
      sets: ex.sets.filter((_, i) => i !== setIndex),
    }));
  };

  const removeExercise = (exId: string) => {
    setSession(prev => ({
      ...prev,
      exercises: prev.exercises.filter(ex => ex.id !== exId),
    }));
  };

  const addExercise = (name: string) => {
    if (!name.trim()) return;
    const newEx: ActiveExercise = {
      id: makeId(),
      name: name.trim(),
      plannedSets: 3,
      plannedReps: '8-12',
      sets: [{ reps: 0, weight: 0, completed: false }],
    };
    setSession(prev => ({ ...prev, exercises: [...prev.exercises, newEx] }));
    setNewExerciseName('');
    setShowAddExercise(false);
  };

  const handleFinish = async () => {
    if (!user?._id) return;
    const exercisesToSave = session.exercises
      .map(ex => ({
        id: ex.id,
        name: ex.name,
        sets: ex.sets.filter(s => s.completed),
      }))
      .filter(ex => ex.sets.length > 0);

    if (exercisesToSave.length === 0) {
      if (!confirm('No sets were completed. Discard this workout?')) return;
      safeSessionRemove('activeWorkoutDraft');
      navigate('/workout-plans?tab=plan');
      return;
    }

    setSaving(true);
    try {
      const log = {
        date: new Date().toLocaleDateString('en-CA'),
        name: session.name,
        exercises: exercisesToSave,
      };
      await progressAPI.createWorkoutLog(user._id, log);
      safeSessionRemove('activeWorkoutDraft');
      navigate('/workout-plans?tab=history');
    } catch (err) {
      console.error('Save workout log error:', err);
      alert('Failed to save workout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!confirm('Cancel this workout? Your progress will be lost.')) return;
    safeSessionRemove('activeWorkoutDraft');
    navigate('/workout-plans?tab=plan');
  };

  const completedSets = session.exercises.reduce((t, ex) => t + ex.sets.filter(s => s.completed).length, 0);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Sticky header */}
      <div className="sticky top-14 z-40 bg-white border-b border-gray-200 shadow-sm -mx-4 px-4 py-3 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <input
              value={session.name}
              onChange={e => setSession(prev => ({ ...prev, name: e.target.value }))}
              className="text-lg font-bold text-gray-900 bg-transparent border-none outline-none w-full truncate"
              placeholder="Workout name"
            />
            <p className="text-sm text-gray-500">
              {formatElapsed(elapsed)} · {completedSets} sets done
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleFinish}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Finish'}
            </button>
          </div>
        </div>
      </div>

      {/* Exercise cards */}
      <div className="space-y-4">
        {session.exercises.map((ex) => (
          <div key={ex.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <input
                value={ex.name}
                onChange={e => updateExercise(ex.id, prev => ({ ...prev, name: e.target.value }))}
                className="font-semibold text-indigo-700 bg-transparent border-none outline-none flex-1 text-base"
                placeholder="Exercise name"
              />
              <button
                onClick={() => removeExercise(ex.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors ml-2"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Set header */}
            <div className="grid grid-cols-12 gap-2 text-xs text-gray-400 font-medium mb-1 px-1">
              <span className="col-span-1">Set</span>
              <span className="col-span-4">Weight (kg)</span>
              <span className="col-span-4">Reps</span>
              <span className="col-span-2 text-center">Done</span>
              <span className="col-span-1"></span>
            </div>

            {/* Sets */}
            {ex.sets.map((set, si) => (
              <div
                key={si}
                className={`grid grid-cols-12 gap-2 items-center px-1 py-1.5 rounded-lg mb-1 transition-colors ${set.completed ? 'bg-green-50' : 'bg-gray-50'}`}
              >
                <span className={`col-span-1 text-sm font-bold ${set.completed ? 'text-green-600' : 'text-gray-400'}`}>{si + 1}</span>
                <input
                  type="number"
                  min={0}
                  value={set.weight || ''}
                  onChange={e => updateSet(ex.id, si, 'weight', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  className="col-span-4 text-sm px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                />
                <input
                  type="number"
                  min={0}
                  value={set.reps || ''}
                  onChange={e => updateSet(ex.id, si, 'reps', parseInt(e.target.value) || 0)}
                  placeholder="0"
                  className="col-span-4 text-sm px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                />
                <button
                  onClick={() => updateSet(ex.id, si, 'completed', !set.completed)}
                  className={`col-span-2 flex items-center justify-center p-1.5 rounded-lg transition-colors ${
                    set.completed
                      ? 'bg-green-500 text-white'
                      : 'bg-white border border-gray-200 text-gray-400 hover:border-green-400 hover:text-green-500'
                  }`}
                >
                  <CheckIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeSet(ex.id, si)}
                  className="col-span-1 flex items-center justify-center p-1 text-gray-300 hover:text-red-400 transition-colors"
                >
                  <TrashIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            <button
              onClick={() => addSet(ex.id)}
              className="mt-2 flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Set
            </button>
          </div>
        ))}
      </div>

      {/* Add Exercise */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        {showAddExercise ? (
          <div className="flex gap-2">
            <input
              autoFocus
              value={newExerciseName}
              onChange={e => setNewExerciseName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addExercise(newExerciseName); if (e.key === 'Escape') setShowAddExercise(false); }}
              placeholder="Exercise name..."
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={() => addExercise(newExerciseName)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => setShowAddExercise(false)}
              className="px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddExercise(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add Exercise
          </button>
        )}
      </div>

      {session.exercises.length === 0 && (
        <p className="text-center text-sm text-gray-400 mt-4">
          Add exercises above or go back to your plan and use "Start This Workout".
        </p>
      )}
    </div>
  );
}
